import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { TeamMemberUpdateStatus } from "@/types";
import { AlertTriangle } from "lucide-react";
import { UpdateFilter, isMissingFocusQueue, needsUpdate } from "./teamFilterConstants";

interface TeamHealthMetricsProps {
  members: TeamMemberUpdateStatus[];
  /** False for someone with no direct reports — there is no team update-health to report on. */
  showTeamHealth: boolean;
  updateFilter: UpdateFilter;
  onUpdateFilterChange: (value: UpdateFilter) => void;
  isLoading: boolean;
}

const pillBase =
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors";
// Two attention colours, by nature of the problem: missing this week's report is the urgent,
// act-now signal (destructive tint); an incomplete board is structural hygiene (amber).
const urgentPill =
  "cursor-pointer border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const urgentActivePill =
  "cursor-pointer border-destructive/60 bg-destructive/20 text-destructive ring-1 ring-destructive/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const warningPill =
  "cursor-pointer border-amber-500/30 bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const warningActivePill =
  "cursor-pointer border-amber-500/60 bg-amber-500/20 text-amber-700 ring-1 ring-amber-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Attention pills only — every pill here is a clickable people-filter. Plain counts (members,
 * priorities) live in the header's description sentence instead: a pill implies an action, and a
 * count you cannot click is not one. Clicking toggles the matching filter
 * (`useTeamPriorityGroups`); clicking the active pill again clears back to "all". At count 0 the
 * pill disappears — zero needs no attention and offers nothing to filter.
 */
export const TeamHealthMetrics = ({
  members,
  showTeamHealth,
  updateFilter,
  onUpdateFilterChange,
  isLoading,
}: TeamHealthMetricsProps) => {
  const needsUpdateCount = members.filter(needsUpdate).length;
  const missingBoardCount = members.filter(isMissingFocusQueue).length;

  const toggle = (filter: UpdateFilter) => onUpdateFilterChange(updateFilter === filter ? "all" : filter);

  if (!showTeamHealth) return null;

  if (isLoading) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-28 rounded-full" />
        ))}
      </div>
    );
  }

  if (needsUpdateCount === 0 && missingBoardCount === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {needsUpdateCount > 0 && (
        <button
          type="button"
          aria-pressed={updateFilter === "needs-update"}
          onClick={() => toggle("needs-update")}
          className={cn(pillBase, updateFilter === "needs-update" ? urgentActivePill : urgentPill)}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {needsUpdateCount} Need update
        </button>
      )}

      {missingBoardCount > 0 && (
        <button
          type="button"
          aria-pressed={updateFilter === "missing-focus-queue"}
          onClick={() => toggle("missing-focus-queue")}
          className={cn(pillBase, updateFilter === "missing-focus-queue" ? warningActivePill : warningPill)}
          title="Every member should hold 1 Focus and 2 Queue items"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {/* This is the FILTER'S name (like "Need update" next to it), not a claim about any one
              person — it covers people failing EITHER half of the rule. The per-member badge
              (`missingFocusQueueLabel`) is what names the exact gap for a specific person. */}
          {missingBoardCount} Missing Focus/Queue
        </button>
      )}
    </div>
  );
};
